import { Alert, Box, Button, Grid, Typography } from "@mui/material";
import { ControlledAutocomplete, FieldControl } from "../../../shared/components";
import { useAddPortfolio } from "../hooks/useAddPortfolio";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { AutocompleteOption, ControlledAutocompleteVariant } from "../../../shared/components/ControlledAutocomplete";
import { useEffect, useState } from "react";
import { ROUTES } from "../../../shared/utils";

export const AddPortfolio: React.FC<{ isEdit: boolean; portfolioId?: string; }> = ({
  isEdit,
  portfolioId,
}) => {

  const navigate = useNavigate();

  const {
    formData,
    fieldErrors,
    handleFieldChange,
    isSubmitting,
    handleSubmit,
    submitMessage,
    setSubmitMessage,
  } = useAddPortfolio({
    isEdit,
    portfolioId,
  });

  const [countdown, setCountdown] = useState<number>(0);

  useEffect(() => {
    if(submitMessage) {
      setCountdown(5);

      const interval = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);

      const timeout = setTimeout(() => {
        navigate(ROUTES.ADD_PROJECT);
      }, 5000);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      }
    }
  }, [submitMessage]);

  if (submitMessage) {
          return (
              <Box m={2} display="flex" justifyContent="center" alignItems="center" height="100vh">
                  <Alert
                      severity={submitMessage.type}
                      onClose={() => setSubmitMessage(null)}
                      sx={{ mb: 3 }}
                  >
                      {submitMessage.message} <br />
                      Redirecting in {countdown} seconds...
                  </Alert>
              </Box>
          );
      }


  return (
    <Box
      p={2}
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "90dvh",
      }}
    >
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
          <Typography variant="body1">Portfolio information</Typography>
          <Typography variant="body2">
            Define the core portfolio details and identifiers
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 8, lg: 8 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <ControlledAutocomplete
                type={"PROGRAMME"}
                value={formData.programme}
                label="Programme"
                onSelect={( value: AutocompleteOption | AutocompleteOption[] | null, type: ControlledAutocompleteVariant) => {
                  handleFieldChange("programme", value)
                }}
                error={!!fieldErrors.programme}
                helperText={fieldErrors.programme}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FieldControl
                variant="input"
                keyName="portfolio_name"
                label="Portfolio Name"
                value={formData.portfolio_name}
                onChange={handleFieldChange}
                error={!!fieldErrors.portfolio_name}
                helperText={fieldErrors.portfolio_name}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FieldControl
                variant="outlined"
                keyName="description"
                label="Description"
                value={formData.description}
                onChange={handleFieldChange}
                error={!!fieldErrors.description}
                helperText={fieldErrors.description}
                multiline
                rows={3}
              />
            </Grid>
            <Grid container spacing={2} sx={{ flex: 1 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FieldControl
                  variant="date"
                  keyName="start_date"
                  label="Start Date"
                  value={formData.start_date}
                  onChange={handleFieldChange}
                  error={!!fieldErrors.start_date}
                  helperText={fieldErrors.start_date}
                  minDate={dayjs()}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FieldControl
                  variant="date"
                  keyName="end_date"
                  label="End Date"
                  value={formData.end_date}
                  onChange={handleFieldChange}
                  error={!!fieldErrors.end_date}
                  helperText={fieldErrors.end_date}
                  minDate={
                    formData.start_date ? dayjs(formData.start_date) : dayjs()
                  }
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={1.5} justifyContent={"end"}>
        <Button
          variant="outlined"
          color="primary"
          disabled={isSubmitting}
          onClick={() => {
            navigate(-1);
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting
            ? `${isEdit ? "Editing" : "Creating"} Portfolio...`
            : isEdit
              ? "Edit Portfolio"
              : "Create Portfolio"}
        </Button>
      </Grid>
    </Box>
  );
};
